import { forwardRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Card from "../../common/components/Card";
import CustomForm from "../../common/components/CustomForm";
import EmailInput from "../../common/components/EmailInput";
import { Col, Row } from "../../common/components/Grid";
import PhoneInput from "../../common/components/PhoneInput";
import ReactForm from "../../common/components/ReactForm";
import TagManager from "../../common/components/TagManager";
import TextInput from "../../common/components/TextInput";
import useClientConfig from "../../common/hooks/clientConfigHook";
import useCredentials from "../../common/hooks/credentialsHook";
import useNetwork from "../../common/hooks/networkHook";
import { ensureInteger } from "../../common/utils/generalUtils";
import { deepMerge } from "../../common/utils/objectUtilities";
import { addContactListMapping, canCreateContact, canRemoveContact, canUpdateContact, createContact, removeContact, removeContactListMapping, updateContact, useContactLists, useContacts, useListsForContact } from "../../reducers/contactSlice";
import SimpleSettings from "./SimpleSettings";
import PropTypes from "prop-types";
import { deepClone } from "../../common/utils/objectUtilities";

const defaultSort = ['name'];
const newContactTemplate = { customfields: {}, lists: [] };

const ContactLabel = ({ value: c }) => {
    return <Row className="px-2">
        <Col className="text-left">
            {c.name}
        </Col>
    </Row>
}
ContactLabel.propTypes = {
    value: PropTypes.object
}

const ContactDetails = forwardRef(({ value: contact, disabled, isNew, onChange }, ref) => {
    const { t } = useTranslation();
    const network = useNetwork();
    const clientConfig = useClientConfig();
    const lists = useContactLists();
    const contactListData = useListsForContact(contact?.id);

    const contactLists = useMemo(() => (isNew ? contact?.lists : contactListData) ?? [], [contactListData, isNew, contact?.lists]);
    const emailLists = useMemo(() => contactLists.filter(cl => cl.type === 'Email').map(cl => cl.id), [contactLists]);
    const mobileLists = useMemo(() => contactLists.filter(cl => cl.type === 'Mobile').map(cl => cl.id), [contactLists]);

    const handleListAdd = type => listid => {
        if (isNew) {
            onChange({ lists: [...contactLists, { id: ensureInteger(listid), type }] });
        } else {
            addContactListMapping(network, contact?.id, listid, type);
        }
    }

    const handleListRemove = type => listid => {
        if (isNew) {
            onChange({ lists: contact.lists.filter(entry => entry.id != listid || entry.type !== type) });
        } else {
            removeContactListMapping(network, contact?.id, listid, type);
        }
    }

    return <ReactForm ref={ref}>
        <Row>
            <Col>
                <TextInput placeholder={t('name')} value={contact?.name} disabled={disabled} onChange={name => onChange({ name })} required></TextInput>
            </Col>
        </Row>
        <Row>
            <Col md={6}>
                <EmailInput placeholder={t('email')} value={contact?.email} disabled={disabled} onChange={email => onChange({ email })}></EmailInput>
            </Col>
            <Col md={6}>
                <PhoneInput placeholder={t('mobile')} value={contact?.mobile} disabled={disabled} onChange={mobile => onChange({ mobile })}></PhoneInput>
            </Col>
        </Row>
        <Row>
            <Col>
                <TextInput placeholder={t('notes')} value={contact?.notes} disabled={disabled} onChange={notes => onChange({ notes })}></TextInput>
            </Col>
        </Row>
        { clientConfig.contactCustomFields && <Row>
            <Col>
                <CustomForm def={clientConfig.contactCustomFields} data={contact?.customfields} disabled={disabled} onChange={delta => onChange({ customfields: deepMerge(deepClone(contact.customfields) ?? {}, delta) })}></CustomForm>
            </Col>
        </Row> }
        <Card title={t('mobileLists')}>
            <TagManager tags={mobileLists} values={lists} labelField="name" placeholder={t('contactList_one')} hideLabel={true} valueField="id" onAdd={handleListAdd('Mobile')} onRemove={handleListRemove('Mobile')}></TagManager>
        </Card>
        <Card title={t('emailLists')}>
            <TagManager tags={emailLists} values={lists} labelField="name" placeholder={t('contactList_one')} hideLabel={true} valueField="id" onAdd={handleListAdd('Email')} onRemove={handleListRemove('Email')}></TagManager>
        </Card>
    </ReactForm>
})
ContactDetails.propTypes = {
    value: PropTypes.object,
    disabled: PropTypes.bool,
    isNew: PropTypes.bool,
    onChange: PropTypes.func
}

const Contacts = ({ uuid, label, onClose }) => {
    const { t } = useTranslation();
    const network = useNetwork();
    const credentials = useCredentials();
    const contacts = useContacts();
    const sortFields = useMemo(() => ({
        name: t('name')
    }), [t])

    const handleAdd = async contact => {
        const toAdd = { ...contact };
        const lists = toAdd.lists;
        delete toAdd.lists;
        const result = await createContact(network, toAdd);
        if (result) {
            lists.forEach(m => addContactListMapping(network, result.id, m.id, m.type));
        }
        return deepClone(newContactTemplate);
    }    

    return <SimpleSettings data={contacts} uuid={uuid} label={label} pkey="id" defaultSort={defaultSort} sortFields={sortFields}
        templateItem={newContactTemplate} canAdd={contact => canCreateContact(credentials, contact)}
        canUpdate={contact => canUpdateContact(credentials, contact)} canRemove={contact => canRemoveContact(credentials, contact)}
        onAdd={handleAdd} onChange={(contact, delta) => updateContact(network, { id: contact.id, ...delta })}
        onRemove={contact => removeContact(network, contact)} onClose={onClose}
        ItemLabel={ContactLabel} ItemDetails={ContactDetails}></SimpleSettings>

}
Contacts.propTypes = {
    /**
     * ID to identify the settings status entry
     */
    uuid: PropTypes.string.isRequired,
    /**
     * Label for the screen
     */
    label: PropTypes.string.isRequired,
    /**
     * Function to call when the screen is closed
     */
    onClose: PropTypes.func
}

export default Contacts;